import { useBookingForTimeRangeChecker } from "@@/products/bookables/hooks/use-booking-for-timeranges";
import { useCheckBookableSession } from "@@/products/bookables/hooks/use-check-bookable-session";
import { useCurrentBookablePrice } from "@@/shared/buttons/bookables/use-current-bookable-price";
import { Column } from "@@/shared/components/column";
import { EmptyState } from "@@/shared/components/empty-state";
import { Row } from "@@/shared/components/row";
import { VirtualizedList } from "@@/shared/components/virtualized-list";
import { TextShimmer } from "@@/shared/pictures/shimmers";
import type { BookableSession, FullDate, TimeRange } from "@towni/common";
import { createSessionStatus, emptyArrayOf, toFullDate } from "@towni/common";
import * as React from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { BookableSessionSelectListSection } from "./bookable-session-select-list-section";

type Props = {
    readonly sessions: BookableSession[];

    // If true, the session will be ignored when checking availability
    readonly ignoreSession?: boolean;
};

const EmptyPlaceholder = () => (
    <Row className="size-full p-5">
        <EmptyState
            className="bg-neutral-surface-10 flex-4 text-center"
            text={{
                sv: "Inga tider tillgängliga",
                en: "No times available",
            }}
        />
    </Row>
);
const Footer = () => <div className="h-25 w-full" />;
const getItemKey = (_: number, fullDate: FullDate) => fullDate;

const BookableSessionSelectList = (props: Props) => {
    const { sessions, ignoreSession } = props;
    const [checkSessionAvailability, isLoading] = useCheckBookableSession();
    const currentPrice = useCurrentBookablePrice();
    const [checkTimeRangeAvailability] = useBookingForTimeRangeChecker();

    const checkTimeRange = React.useCallback(
        (timeRange: TimeRange) => {
            const val = checkTimeRangeAvailability(timeRange, ignoreSession);

            return val;
        },
        [ignoreSession, checkTimeRangeAvailability],
    );

    const sessionsByFullDate = React.useMemo(() => {
        return sessions.reduce(
            (prev, current) => {
                const fullDate = toFullDate(current.start);
                prev[fullDate] = prev[fullDate] || [];
                prev[fullDate].push(current);

                return prev;
            },
            {} as Record<FullDate, BookableSession[]>,
        );
    }, [sessions]);

    const [fullDates, setFullDates] =
        React.useState<FullDate[]>(emptyArrayOf<FullDate>());

    const prevFullDates = React.useRef(fullDates);
    React.useEffect(() => {
        // Compare if there are changes
        const oldFullDates = prevFullDates.current;
        const newFullDates = Object.keys(
            sessionsByFullDate,
        ) as unknown as FullDate[];

        if (
            oldFullDates.length === newFullDates.length &&
            oldFullDates.every(
                (fullDate, index) => newFullDates[index] === fullDate,
            )
        ) {
            return;
        }
        setFullDates(newFullDates);
        prevFullDates.current = newFullDates;
    }, [sessionsByFullDate]);

    const listRef = React.useRef<VirtuosoHandle | null>(null);

    React.useEffect(() => {
        listRef.current?.scrollToIndex(0);
    }, [fullDates]);

    const renderItem = React.useCallback(
        (_: number, fullDate: FullDate) => {
            //checkAvailability
            const sessions = sessionsByFullDate[fullDate];
            if (!sessions) return <TextShimmer rows={2} />;
            //checkAvailability

            const sessionsAvailability = sessions.map(item => {
                const resourceAvailability = checkSessionAvailability(
                    item,
                    undefined,
                    ignoreSession,
                );
                const isBookable =
                    !resourceAvailability.resourceAvailabilities.some(
                        a => !a.available,
                    );

                if (isBookable) return item;
                // If not bookable, set session to full and unbookable
                return setSessionToFull(item);
            });

            return (
                <BookableSessionSelectListSection
                    key={fullDate}
                    sessions={sessionsAvailability}
                    checkAvailability={checkTimeRange}
                    isLoading={isLoading}
                    currentPrice={currentPrice}
                />
            );
        },
        [
            checkSessionAvailability,
            checkTimeRange,
            currentPrice,
            ignoreSession,
            isLoading,
            sessionsByFullDate,
        ],
    );

    return (
        <Column className="w-full flex-shrink flex-grow overflow-hidden">
            <VirtualizedList
                ref={listRef}
                className="flex-1"
                key="session-list"
                data={fullDates}
                itemContent={renderItem}
                computeItemKey={getItemKey}
                components={{
                    EmptyPlaceholder,
                    Footer,
                }}
            />
        </Column>
    );
};

const setSessionToFull = (session: BookableSession) => {
    return {
        ...session,
        status: createSessionStatus({
            type: "FULL",
        }),
    };
};

export { BookableSessionSelectList };
